import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Alert, Button, Card, Field, Page, PageHeader } from '@evogard/ui';
import { MIN_PASSWORD_LENGTH } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';

/**
 * Trocar a própria senha, estando logado (30/10/2026).
 *
 * Pede a atual: sem ela, uma aba esquecida aberta troca a senha e tranca o dono
 * do lado de fora. Esta aba continua dentro — foi ela que provou saber a
 * senha —, e as outras sessões caem no servidor, por isso a lista de
 * "Minhas sessões" é relida.
 */
export function PasswordScreen() {
  const client = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: async () =>
      ok(
        await api.platform.changePassword({
          body: { passwordCurrent: current, passwordNew: next },
        }),
        200,
        'Não foi possível trocar a senha.',
      ),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirmation('');
      void client.invalidateQueries({ queryKey: ['plataforma', 'sessoes'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const nextShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmation.length > 0 && next !== confirmation;

  function send(e: FormEvent) {
    e.preventDefault();
    setError(null);
    change.mutate();
  }

  return (
    <Page>
      <PageHeader title="Trocar a senha">
        As outras sessões são encerradas; esta continua aberta.
      </PageHeader>

      <Card>
        <form onSubmit={send} className="flex max-w-sm flex-col gap-4">
          {error && <Alert tone="erro">{error}</Alert>}
          {change.isSuccess && <Alert tone="sucesso">{change.data.message}</Alert>}

          <Field
            label="Senha atual"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => {
              change.reset();
              setCurrent(e.target.value);
            }}
          />
          <Field
            label="Nova senha"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={next}
            onChange={(e) => {
              change.reset();
              setNext(e.target.value);
            }}
            error={nextShort ? `Use pelo menos ${MIN_PASSWORD_LENGTH} caracteres.` : null}
            help={`Pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`}
          />
          <Field
            label="Repita a nova senha"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            error={mismatch ? 'As duas não são iguais.' : null}
          />

          <div>
            <Button
              type="submit"
              loading={change.isPending}
              disabled={
                !current || next.length < MIN_PASSWORD_LENGTH || next !== confirmation
              }
            >
              Trocar a senha
            </Button>
          </div>
        </form>
      </Card>
    </Page>
  );
}
